<template>
  <div class="system-param-container">
    <div class="header">
      <h2>{{ $t('system.paramManagement') }}</h2>
    </div>

    <el-form ref="formRef" :model="form" :rules="rules" label-width="180px">
      <el-card class="param-card">
        <div slot="header" class="card-header">
          <span>{{ $t('system.limitConfig') }}</span>
        </div>
        <el-form-item :label="$t('system.asrDurationLimit')" prop="asrDurationLimit">
          <div class="asr-row">
            <div class="asr-left">
              <el-input-number
                v-model="form.asrDurationLimit"
                :min="0"
                :step="1000"
                controls-position="right"
                style="width: 200px"
              />
              <span class="form-tip">{{ $t('system.milliseconds') }}</span>
            </div>
            <div class="asr-right-app">
              <span class="app-url-label">{{ $t('system.appDownloadUrl') }}：</span>
              <el-input
                v-model="form.appDownloadUrl"
                :placeholder="$t('system.uploadAfterAutoGenerate')"
                clearable
                style="width: 260px; margin-right: 10px"
                disabled
              />
              <div class="upload-app-container">
                <el-upload
                  :action="uploadAppAction"
                  :show-file-list="false"
                  :on-success="handleAppUploadSuccess"
                  :on-error="handleAppUploadError"
                  :on-progress="handleAppUploadProgress"
                  :before-upload="beforeAppUpload"
                  :headers="uploadHeaders"
                  :auto-upload="true"
                  accept=".apk,.exe,.dmg,.msi,.zip"
                >
                  <el-button size="small" type="primary" :loading="appUploading">
                    <i class="el-icon-upload2"></i>
                    {{ $t('system.uploadApp') }}
                  </el-button>
                </el-upload>
                <el-progress
                  v-if="appUploading"
                  :percentage="appUploadProgress"
                  :status="appUploadProgress === 100 ? 'success' : null"
                  :stroke-width="6"
                  style="width: 200px; margin-top: 8px;"
                ></el-progress>
              </div>
            </div>
          </div>
        </el-form-item>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.ttsTokenLimit')" prop="ttsTokenLimit">
              <el-input-number
                v-model="form.ttsTokenLimit"
                :min="0"
                :step="1000"
                controls-position="right"
                style="width: 100%"
              />
              <span class="form-tip">{{ $t('system.tokenCount') }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.version')" prop="version">
              <el-input
                v-model="form.version"
                :placeholder="$t('system.versionPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.llmTokenLimit')" prop="llmTokenLimit">
              <el-input-number
                v-model="form.llmTokenLimit"
                :min="0"
                :step="1000"
                controls-position="right"
                style="width: 100%"
              />
              <span class="form-tip">{{ $t('system.tokenCount') }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.limitValidPeriodHours')" prop="limitValidPeriodHours">
              <el-input-number
                v-model="form.limitValidPeriodHours"
                :min="1"
                :step="1"
                controls-position="right"
                style="width: 100%"
              />
              <span class="form-tip">{{ $t('system.hours') }}</span>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.catalogHash')" prop="catalogHash">
              <el-input
                v-model="form.catalogHash"
                :placeholder="$t('system.catalogHashPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.catalogUrl')" prop="catalogUrl">
              <el-input
                v-model="form.catalogUrl"
                :placeholder="$t('system.catalogUrlPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item label="完整性校验哈希值" prop="integrityHash">
              <el-input
                v-model="form.integrityHash"
                placeholder="请输入完整性校验哈希值"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="完整性校验URL" prop="integrityUrl">
              <el-input
                v-model="form.integrityUrl"
                placeholder="请输入完整性校验URL"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="action-bar">
          <el-button type="primary" @click="onSubmit" :loading="saving" size="medium">
            <i class="el-icon-check"></i>
            {{ $t('system.saveSettings') }}
          </el-button>
        </div>
      </el-card>

      <el-card
        v-for="(config, index) in qwenConfigs"
        :key="config.environment || index"
        class="param-card"
        style="margin-top: 20px"
      >
        <div slot="header" class="card-header">
          <span>{{ $t('system.qwenConfig') }} - {{ getEnvironmentTitle(config.environment) }}</span>
        </div>
        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenApiKey')" :prop="`qwenConfigs.${index}.qwenApiKey`">
              <el-input
                v-model="config.qwenApiKey"
                :placeholder="$t('system.qwenApiKeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenApiUrl')" :prop="`qwenConfigs.${index}.qwenApiUrl`">
              <el-input
                v-model="config.qwenApiUrl"
                :placeholder="$t('system.qwenApiUrlPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenModel')" :prop="`qwenConfigs.${index}.qwenModel`">
              <el-input
                v-model="config.qwenModel"
                :placeholder="$t('system.qwenModelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenTtsApiKey')" :prop="`qwenConfigs.${index}.qwenTtsApiKey`">
              <el-input
                v-model="config.qwenTtsApiKey"
                :placeholder="$t('system.qwenTtsApiKeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenTtsModel')" :prop="`qwenConfigs.${index}.qwenTtsModel`">
              <el-input
                v-model="config.qwenTtsModel"
                :placeholder="$t('system.qwenTtsModelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenAsrApiKey')" :prop="`qwenConfigs.${index}.qwenAsrApiKey`">
              <el-input
                v-model="config.qwenAsrApiKey"
                :placeholder="$t('system.qwenAsrApiKeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.qwenAsrModel')" :prop="`qwenConfigs.${index}.qwenAsrModel`">
              <el-input
                v-model="config.qwenAsrModel"
                :placeholder="$t('system.qwenAsrModelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.toolAgentApiKey')" :prop="`qwenConfigs.${index}.toolAgentApiKey`">
              <el-input
                v-model="config.toolAgentApiKey"
                :placeholder="$t('system.toolAgentApiKeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.toolAgentModel')" :prop="`qwenConfigs.${index}.toolAgentModel`">
              <el-input
                v-model="config.toolAgentModel"
                :placeholder="$t('system.toolAgentModelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.actionAgentApiKey')" :prop="`qwenConfigs.${index}.actionAgentApiKey`">
              <el-input
                v-model="config.actionAgentApiKey"
                :placeholder="$t('system.actionAgentApiKeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.actionAgentModel')" :prop="`qwenConfigs.${index}.actionAgentModel`">
              <el-input
                v-model="config.actionAgentModel"
                :placeholder="$t('system.actionAgentModelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.imageAgentApiKey')" :prop="`qwenConfigs.${index}.imageAgentApiKey`">
              <el-input
                v-model="config.imageAgentApiKey"
                :placeholder="$t('system.imageAgentApiKey')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.imageAgentModel')" :prop="`qwenConfigs.${index}.imageAgentModel`">
              <el-input
                v-model="config.imageAgentModel"
                :placeholder="$t('system.imageAgentModel')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
          </el-col>
        </el-row>

        <el-row :gutter="40">
          <el-col :span="12">
            <el-form-item :label="$t('system.ttskey')" :prop="`qwenConfigs.${index}.ttskey`">
              <el-input
                v-model="config.ttskey"
                :placeholder="$t('system.ttskeyPlaceholder')"
                style="width: 100%"
                show-password
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('system.ttsmodel')" :prop="`qwenConfigs.${index}.ttsmodel`">
              <el-input
                v-model="config.ttsmodel"
                :placeholder="$t('system.ttsmodelPlaceholder')"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="action-bar">
          <el-button
            type="primary"
            @click="onSaveQwenConfig(index)"
            :loading="config.saving"
            size="medium"
          >
            <i class="el-icon-check"></i>
            {{ $t('system.saveSettings') }}
          </el-button>
        </div>
      </el-card>

      <el-card class="param-card" style="margin-top: 20px">
        <div slot="header" class="card-header">
          <span>{{ $t('system.systemPrompt') }}</span>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            style="float: right"
            @click="handleAddLanguageFormat"
          >
            {{ $t('system.addLanguageFormat') }}
          </el-button>
        </div>
        <el-table
          v-loading="languageFormatLoading"
          :data="languageFormats"
          border
          style="width: 100%"
          :header-cell-style="{
            background: '#404761',
            color: '#fff',
            borderBottom: '1px solid #4A4A6A',
            fontWeight: '500'
          }"
          :cell-style="{
            background: 'transparent',
            color: '#fff',
            borderBottom: '1px solid #4A4A6A'
          }"
        >
          <el-table-column prop="id" :label="$t('common.id')" width="80" align="center" />
          <el-table-column prop="languageCode" :label="$t('system.languageCode')" width="120" align="center" />
          <el-table-column prop="languageName" :label="$t('system.languageName')" width="150" />
          <el-table-column prop="responseFormat" :label="$t('system.prompt')" min-width="300" show-overflow-tooltip />
          <el-table-column prop="systemPromptTemplate" :label="$t('system.systemPromptTemplate')" min-width="250" show-overflow-tooltip />
          <el-table-column :label="$t('common.operation')" width="150" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="small"
                icon="el-icon-edit"
                @click="handleEditLanguageFormat(scope.row)"
              >
                {{ $t('common.edit') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </el-form>

    <!-- 语言响应格式配置对话框 -->
    <el-dialog
      :title="languageFormatDialogTitle"
      :visible.sync="languageFormatDialogVisible"
      width="800px"
      :close-on-click-modal="false"
    >
      <el-form
        ref="languageFormatFormRef"
        :model="languageFormatForm"
        :rules="languageFormatRules"
        label-width="180px"
      >
        <el-form-item :label="$t('system.languageCode')" prop="languageCode">
          <el-input
            v-model="languageFormatForm.languageCode"
            :placeholder="$t('system.languageCodePlaceholder')"
            :disabled="!!languageFormatForm.id"
            style="width: 300px"
          />
          <div class="form-desc">{{ $t('system.languageCodeDesc') }}</div>
        </el-form-item>
        <el-form-item :label="$t('system.languageName')" prop="languageName">
          <el-input
            v-model="languageFormatForm.languageName"
            :placeholder="$t('system.languageNamePlaceholder')"
            style="width: 300px"
          />
        </el-form-item>
        <el-form-item :label="$t('system.prompt')" prop="responseFormat">
          <el-input
            v-model="languageFormatForm.responseFormat"
            type="textarea"
            :rows="6"
            :placeholder="$t('system.promptPlaceholder')"
          />
          <div class="form-desc">{{ $t('system.promptDesc') }}</div>
        </el-form-item>
        <el-form-item :label="$t('system.systemPromptTemplate')" prop="systemPromptTemplate">
          <el-input
            v-model="languageFormatForm.systemPromptTemplate"
            type="textarea"
            :rows="4"
            :placeholder="$t('system.systemPromptTemplatePlaceholder')"
          />
          <div class="form-desc">{{ $t('system.systemPromptTemplateDesc') }}</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="languageFormatDialogVisible = false">
          {{ $t('common.cancel') }}
        </el-button>
        <el-button type="primary" @click="handleSaveLanguageFormat" :loading="languageFormatSaving">
          {{ $t('common.save') }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/network/system/index'

export default {
  name: 'SystemParam',
  data() {
    return {
      loading: false,
      saving: false,
      form: {
        id: undefined,
        asrDurationLimit: 3600000,
        llmTokenLimit: 100000,
        ttsTokenLimit: 50000,
        limitValidPeriodHours: 24,
        appDownloadUrl: '',
        version: '',
        catalogUrl: '',
        catalogHash: '',
        integrityUrl: '',
        integrityHash: ''
      },
      originalForm: {},
      // Qwen配置列表
      qwenConfigs: [],
      uploadAppAction: '/api/laplace/system/uploadApp',
      appUploading: false,
      appUploadProgress: 0,
      rules: {
        asrDurationLimit: [
          { required: true, message: '请输入ASR时长限制', trigger: 'blur' },
          { type: 'number', min: 0, message: 'ASR时长限制必须大于等于0', trigger: 'change' }
        ],
        llmTokenLimit: [
          { required: true, message: '请输入LLM Token限制', trigger: 'blur' },
          { type: 'number', min: 0, message: 'LLM Token限制必须大于等于0', trigger: 'change' }
        ],
        ttsTokenLimit: [
          { required: true, message: '请输入TTS Token限制', trigger: 'blur' },
          { type: 'number', min: 0, message: 'TTS Token限制必须大于等于0', trigger: 'change' }
        ],
        limitValidPeriodHours: [
          { required: true, message: '请输入限制有效期', trigger: 'blur' },
          { type: 'number', min: 1, message: '限制有效期必须大于等于1小时', trigger: 'change' }
        ]
      },
      // 语言响应格式配置相关
      languageFormats: [],
      languageFormatLoading: false,
      languageFormatDialogVisible: false,
      languageFormatDialogTitle: '',
      languageFormatSaving: false,
      languageFormatForm: {
        id: undefined,
        languageCode: '',
        languageName: '',
        responseFormat: '',
        systemPromptTemplate: ''
      }
    }
  },
  computed: {
    uploadHeaders() {
      const token = this.$store.state.token
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    languageFormatRules() {
      return {
        languageCode: [
          { required: true, message: this.$t('system.validationLanguageCodeRequired'), trigger: 'blur' },
          { pattern: /^([a-z]{2}|yue)$/, message: this.$t('system.validationLanguageCodeFormat'), trigger: 'blur' }
        ],
        languageName: [
          { required: true, message: this.$t('system.validationLanguageNameRequired'), trigger: 'blur' }
        ],
        responseFormat: [
          { required: true, message: this.$t('system.validationPromptRequired'), trigger: 'blur' }
        ],
        systemPromptTemplate: [
          { required: true, message: this.$t('system.validationSystemPromptTemplateRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (!value) {
                callback()
                return
              }
              if (!value.includes('%s')) {
                callback(new Error(this.$t('system.validationSystemPromptTemplatePlaceholder')))
                return
              }
              callback()
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  created() {
    this.fetchData()
    this.fetchLanguageFormats()
    this.fetchQwenConfigs()
  },
  methods: {
    beforeAppUpload(file) {
      const isValidType = ['.apk', '.exe', '.dmg', '.msi', '.zip'].some(ext =>
        file.name.toLowerCase().endsWith(ext)
      )
      if (!isValidType) {
        this.$message.error('只能上传 .apk、.exe、.dmg、.msi、.zip 格式的文件')
        return false
      }
      this.appUploading = true
      this.appUploadProgress = 0
      return true
    },
    handleAppUploadProgress(event, file, fileList) {
      this.appUploadProgress = Math.round((event.loaded / event.total) * 100)
    },
    handleAppUploadSuccess(res) {
      this.appUploading = false
      if (res.statusCode === 100200) {
        const appUrl = res.data?.url || res.data
        this.form.appDownloadUrl = appUrl
        this.$message.success('APP 上传成功，下载地址已更新')
        setTimeout(() => {
          this.appUploadProgress = 0
        }, 1000)
      } else {
        this.$message.error(res.message || 'APP 上传失败')
        this.appUploadProgress = 0
      }
    },
    handleAppUploadError(err, file, fileList) {
      this.appUploading = false
      this.appUploadProgress = 0
      this.$message.error('APP 上传失败: ' + (err.message || '未知错误'))
    },
    async fetchData() {
      this.loading = true
      try {
        const res = await api.getSystemParams()
        if (res.statusCode === 100200 && res.data) {
          this.form = {
            id: res.data.id || undefined,
            asrDurationLimit: res.data.asrDurationLimit || 3600000,
            llmTokenLimit: res.data.llmTokenLimit || 100000,
            ttsTokenLimit: res.data.ttsTokenLimit || 50000,
            limitValidPeriodHours: res.data.limitValidPeriodHours || 24,
            appDownloadUrl: res.data.appDownloadUrl || '',
            version: res.data.version || '',
            catalogUrl: res.data.catalogUrl || '',
            catalogHash: res.data.catalogHash || '',
            integrityUrl: res.data.integrityUrl || '',
            integrityHash: res.data.integrityHash || ''
          }
          // 保存原始数据用于重置
          this.originalForm = JSON.parse(JSON.stringify(this.form))
        }
      } catch (error) {
        console.error('获取系统参数失败:', error)
        this.$message.error('获取系统参数失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    async onSubmit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) {
          this.$message.warning('请检查表单填写是否正确')
          return
        }

        this.saving = true
        try {
          const res = await api.updateSystemParams(this.form)
          if (res.statusCode === 100200) {
            this.$message.success('保存成功')
            // 更新原始数据
            this.originalForm = JSON.parse(JSON.stringify(this.form))
          } else {
            this.$message.error(res.message || '保存失败')
          }
        } catch (error) {
          console.error('保存失败:', error)
          this.$message.error('保存失败: ' + (error.message || '未知错误'))
        } finally {
          this.saving = false
        }
      })
    },
    onReset() {
      this.$confirm('确认重置所有修改？', '提示', { type: 'warning' })
        .then(() => {
          this.form = JSON.parse(JSON.stringify(this.originalForm))
          this.$refs.formRef && this.$refs.formRef.clearValidate()
          this.$message.success('已重置')
        })
        .catch(() => {})
    },
    // Qwen配置相关方法
    async fetchQwenConfigs() {
      try {
        const res = await api.getConfigs()
        if (res.statusCode === 100200 && res.data && Array.isArray(res.data)) {
          // 确保有且只有两条数据，并按port和remote顺序排列
          const configs = res.data.slice(0, 2)
          const portConfig = configs.find(c => c.environment === 'port') || {
            environment: 'port',
            qwenApiKey: '',
            qwenApiUrl: '',
            qwenModel: '',
            qwenTtsApiKey: '',
            qwenTtsModel: '',
            qwenAsrApiKey: '',
            qwenAsrModel: '',
            toolAgentApiKey: '',
            toolAgentModel: '',
            actionAgentApiKey: '',
            actionAgentModel: '',
            imageAgentApiKey: '',
            imageAgentModel: '',
            ttskey: '',
            ttsmodel: '',
            saving: false
          }
          const remoteConfig = configs.find(c => c.environment === 'remote') || {
            environment: 'remote',
            qwenApiKey: '',
            qwenApiUrl: '',
            qwenModel: '',
            qwenTtsApiKey: '',
            qwenTtsModel: '',
            qwenAsrApiKey: '',
            qwenAsrModel: '',
            toolAgentApiKey: '',
            toolAgentModel: '',
            actionAgentApiKey: '',
            actionAgentModel: '',
            imageAgentApiKey: '',
            imageAgentModel: '',
            ttskey: '',
            ttsmodel: '',
            saving: false
          }
          // 确保每个配置都有saving属性和新字段的默认值
          if (!portConfig.hasOwnProperty('saving')) {
            portConfig.saving = false
          }
          if (!remoteConfig.hasOwnProperty('saving')) {
            remoteConfig.saving = false
          }
          // 确保所有字段存在
          const fields = [
            'qwenApiKey', 'qwenApiUrl', 'qwenModel', 
            'qwenTtsApiKey', 'qwenTtsModel', 
            'qwenAsrApiKey', 'qwenAsrModel',
            'toolAgentApiKey', 'toolAgentModel',
            'actionAgentApiKey', 'actionAgentModel',
            'imageAgentApiKey', 'imageAgentModel',
            'ttskey', 'ttsmodel'
          ]
          fields.forEach(field => {
            if (portConfig[field] === undefined) portConfig[field] = ''
            if (remoteConfig[field] === undefined) remoteConfig[field] = ''
          })
          this.qwenConfigs = [portConfig, remoteConfig]
        } else {
          // 如果接口失败或数据格式不对，初始化两条空数据
          const defaultConfig = (env) => ({
            environment: env,
            qwenApiKey: '',
            qwenApiUrl: '',
            qwenModel: '',
            qwenTtsApiKey: '',
            qwenTtsModel: '',
            qwenAsrApiKey: '',
            qwenAsrModel: '',
            toolAgentApiKey: '',
            toolAgentModel: '',
            actionAgentApiKey: '',
            actionAgentModel: '',
            imageAgentApiKey: '',
            imageAgentModel: '',
            ttskey: '',
            ttsmodel: '',
            saving: false
          })
          this.qwenConfigs = [defaultConfig('port'), defaultConfig('remote')]
        }
      } catch (error) {
        console.error('获取Qwen配置失败:', error)
        // 如果接口失败，初始化两条空数据
        const defaultConfig = (env) => ({
          environment: env,
          qwenApiKey: '',
          qwenApiUrl: '',
          qwenModel: '',
          qwenTtsApiKey: '',
          qwenTtsModel: '',
          qwenAsrApiKey: '',
          qwenAsrModel: '',
          toolAgentApiKey: '',
          toolAgentModel: '',
          actionAgentApiKey: '',
          actionAgentModel: '',
          imageAgentApiKey: '',
          imageAgentModel: '',
          ttskey: '',
          ttsmodel: '',
          saving: false
        })
        this.qwenConfigs = [defaultConfig('port'), defaultConfig('remote')]
      }
    },
    getEnvironmentTitle(environment) {
      if (environment === 'port') {
        return this.$t('system.environmentDomestic')
      } else if (environment === 'remote') {
        return this.$t('system.environmentOverseas')
      }
      return environment || this.$t('system.environmentUnknown')
    },
    // 单独保存Qwen配置
    async onSaveQwenConfig(index) {
      const config = this.qwenConfigs[index]
      if (!config) {
        this.$message.error('配置数据不存在')
        return
      }

      // 设置当前配置的保存状态
      this.$set(config, 'saving', true)
      try {
        const res = await api.updateConfig(config)
        if (res.statusCode === 100200) {
          this.$message.success('保存成功')
          // 重新获取配置以更新数据
          await this.fetchQwenConfigs()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        console.error('保存Qwen配置失败:', error)
        this.$message.error('保存失败: ' + (error.message || '未知错误'))
      } finally {
        this.$set(config, 'saving', false)
      }
    },
    // 语言响应格式配置相关方法
    async fetchLanguageFormats() {
      this.languageFormatLoading = true
      try {
        const res = await api.getAllLanguageResponseFormats()
        if (res.statusCode === 100200 && res.data) {
          this.languageFormats = res.data || []
        } else {
          this.$message.error(res.message || '获取语言响应格式配置失败')
        }
      } catch (error) {
        console.error('获取语言响应格式配置失败:', error)
        this.$message.error('获取语言响应格式配置失败: ' + (error.message || '未知错误'))
      } finally {
        this.languageFormatLoading = false
      }
    },
    handleAddLanguageFormat() {
      this.languageFormatDialogTitle = this.$t('system.addLanguageFormat')
      this.languageFormatForm = {
        id: undefined,
        languageCode: '',
        languageName: '',
        responseFormat: '',
        systemPromptTemplate: ''
      }
      this.languageFormatDialogVisible = true
      this.$nextTick(() => {
        this.$refs.languageFormatFormRef && this.$refs.languageFormatFormRef.clearValidate()
      })
    },
    handleEditLanguageFormat(row) {
      this.languageFormatDialogTitle = this.$t('system.editLanguageFormat')
      this.languageFormatForm = {
        id: row.id,
        languageCode: row.languageCode,
        languageName: row.languageName,
        responseFormat: row.responseFormat,
        systemPromptTemplate: row.systemPromptTemplate
      }
      this.languageFormatDialogVisible = true
      this.$nextTick(() => {
        this.$refs.languageFormatFormRef && this.$refs.languageFormatFormRef.clearValidate()
      })
    },
    handleSaveLanguageFormat() {
      this.$refs.languageFormatFormRef.validate(async valid => {
        if (!valid) {
          this.$message.warning('请检查表单填写是否正确')
          return
        }

        this.languageFormatSaving = true
        try {
          let res
          if (this.languageFormatForm.id) {
            // 更新
            res = await api.updateLanguageResponseFormat(this.languageFormatForm)
          } else {
            // 新增
            res = await api.addLanguageResponseFormat(this.languageFormatForm)
          }

          if (res.statusCode === 100200) {
            this.$message.success(this.languageFormatForm.id ? '修改成功' : '添加成功')
            this.languageFormatDialogVisible = false
            this.fetchLanguageFormats()
          } else {
            this.$message.error(res.message || (this.languageFormatForm.id ? '修改失败' : '添加失败'))
          }
        } catch (error) {
          console.error('保存失败:', error)
          this.$message.error('保存失败: ' + (error.message || '未知错误'))
        } finally {
          this.languageFormatSaving = false
        }
      })
    }
  }
}
</script>

<style scoped>
.system-param-container {
  padding: 20px;
  background: #232A3E;
  min-height: calc(100vh - 84px);
  color: #fff;
}

.header {
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 500;
  color: #fff;
}

.param-card {
  background: #333A53;
  border: 1px solid #4A4A6A;
}

.card-header {
  font-size: 16px;
  font-weight: 500;
  color: #fff;
}

.form-tip {
  margin-left: 10px;
  color: #9CA3AF;
  font-size: 14px;
}

.form-desc {
  margin-top: 5px;
  color: #9CA3AF;
  font-size: 12px;
  line-height: 1.5;
}

.asr-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.asr-left {
  display: flex;
  flex-direction: column;
}

.asr-right-app {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.upload-app-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.app-url-label {
  margin-right: 8px;
  color: #E5E7EB;
  font-size: 14px;
}

.action-bar {
  margin-top: 20px;
  text-align: center;
  padding: 20px 0;
  border-top: 1px solid #4A4A6A;
}

.action-bar .el-button {
  margin: 0 10px;
}

/* Element UI 组件深色主题覆盖 */
::v-deep .el-card {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
}

::v-deep .el-card__header {
  background-color: #404761 !important;
  border-bottom: 1px solid #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-card__body {
  background-color: #333A53 !important;
  color: #fff !important;
}

::v-deep .el-form-item__label {
  color: #E5E7EB !important;
}

::v-deep .el-input__inner {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-input__inner:focus {
  border-color: #6A33F0 !important;
}

::v-deep .el-input.is-disabled .el-input__inner {
  background-color: #2A2A3F !important;
  border-color: #4A4A6A !important;
  color: #9CA3AF !important;
}

::v-deep .el-input-number .el-input__inner {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-input-number__decrease,
::v-deep .el-input-number__increase {
  background-color: #404761 !important;
  border-color: #4A4A6A !important;
  color: #E5E7EB !important;
}

::v-deep .el-input-number__decrease:hover,
::v-deep .el-input-number__increase:hover {
  color: #6A33F0 !important;
}

::v-deep .el-textarea__inner {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-textarea__inner:focus {
  border-color: #6A33F0 !important;
}

::v-deep .el-select .el-input__inner {
  background-color: #333A53 !important;
  border-color: #4A4A6A !important;
  color: #fff !important;
}

/* 表格深色主题样式 - 极客暗黑专业版 */
::v-deep .el-table {
  background-color: #0F172A !important;
  color: #F8FAFC !important;
  border-color: #1E293B !important;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.4);
}

::v-deep .el-table::before {
  background-color: #4A4A6A !important;
}

::v-deep .el-table--border::after {
  background-color: #4A4A6A !important;
}

::v-deep .el-table--border {
  border-color: #4A4A6A !important;
}

::v-deep .el-table th.el-table__cell {
  background-color: #404761 !important;
  border-bottom: 1px solid #4A4A6A !important;
  border-right: 1px solid #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-table th.el-table__cell .cell {
  color: #fff !important;
}

::v-deep .el-table th.el-table__cell > div {
  color: #fff !important;
}

::v-deep .el-table__header th .cell {
  color: #fff !important;
}

::v-deep .el-table__header th > div {
  color: #fff !important;
}

::v-deep .el-table th.el-table__cell:last-child {
  border-right: none !important;
}

::v-deep .el-table td.el-table__cell {
  background-color: transparent !important;
  border-bottom: 1px solid #4A4A6A !important;
  border-right: 1px solid #4A4A6A !important;
  color: #fff !important;
}

::v-deep .el-table td.el-table__cell:last-child {
  border-right: none !important;
}

/* 表格单元格内容颜色 - 更具体的选择器 */
::v-deep .el-table td.el-table__cell .cell {
  color: #fff !important;
}

::v-deep .el-table td.el-table__cell > div {
  color: #fff !important;
}

::v-deep .el-table__body td.el-table__cell .cell {
  color: #fff !important;
}

::v-deep .el-table__body td.el-table__cell > div {
  color: #fff !important;
}

::v-deep .el-table__body-wrapper td .cell {
  color: #fff !important;
}

::v-deep .el-table__body-wrapper td > div {
  color: #fff !important;
}

/* 确保所有表格单元格内的文本都是浅色 */
::v-deep .el-table__body td {
  color: #fff !important;
}

::v-deep .el-table__body td span {
  color: #fff !important;
}

::v-deep .el-table__header-wrapper {
  background-color: transparent !important;
}

::v-deep .el-table__body-wrapper {
  background-color: transparent !important;
}

::v-deep .el-table tr {
  background-color: transparent !important;
}

::v-deep .el-table th.el-table__cell {
  background: linear-gradient(180deg, #1E293B 0%, #0F172A 100%) !important;
  color: #8B5CF6 !important;
  border-bottom: 2px solid #334155 !important;
  border-right: 1px solid #1E293B !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

::v-deep .el-table td.el-table__cell {
  background-color: transparent !important;
  border-bottom: 1px solid #1E293B !important;
  border-right: 1px solid #1E293B !important;
  color: #F1F5F9 !important;
}

::v-deep .el-table__body tr:hover > td {
  background-color: rgba(139, 92, 246, 0.1) !important;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: rgba(30, 41, 59, 0.5) !important;
}

::v-deep .el-table__body tr:hover > td .cell,
::v-deep .el-table__body tr:hover > td > div,
::v-deep .el-table__body tr:hover > td span,
::v-deep .el-table__body tr:hover > td * {
  color: #A78BFA !important;
}

::v-deep .el-table__empty-block {
  background-color: transparent !important;
}

::v-deep .el-table__empty-text {
  color: #9CA3AF !important;
}

::v-deep .el-loading-mask {
  background-color: rgba(35, 42, 62, 0.8) !important;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: rgba(64, 71, 97, 0.3) !important;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped:hover > td {
  background-color: rgba(106, 51, 240, 0.15) !important;
}

/* 表格按钮样式 */
::v-deep .el-table .el-button--text {
  color: #6A33F0 !important;
  padding: 0;
}

::v-deep .el-table .el-button--text:hover {
  color: #7b47ff !important;
}

::v-deep .el-table .el-button--text.el-button--small {
  font-size: 14px;
}

/* 表格固定列样式 */
::v-deep .el-table__fixed,
::v-deep .el-table__fixed-right {
  background-color: transparent !important;
}

::v-deep .el-table__fixed::before,
::v-deep .el-table__fixed-right::before {
  background-color: #4A4A6A !important;
}

/* 表格滚动条样式 */
::v-deep .el-table__body-wrapper::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-track {
  background: #333A53;
  border-radius: 3px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #6A33F0;
  border-radius: 3px;
}

::v-deep .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: #7b47ff;
}

::v-deep .el-button--primary {
  background-color: #6A33F0 !important;
  border-color: #6A33F0 !important;
}

::v-deep .el-button--primary:hover {
  background-color: #7b47ff !important;
  border-color: #7b47ff !important;
}

::v-deep .el-dialog {
  background-color: #333A53 !important;
  border: 1px solid #4A4A6A !important;
}

::v-deep .el-dialog__header {
  background-color: #404761 !important;
  border-bottom: 1px solid #4A4A6A !important;
}

::v-deep .el-dialog__title {
  color: #fff !important;
}

::v-deep .el-dialog__body {
  background-color: #333A53 !important;
  color: #E5E7EB !important;
}

::v-deep .el-dialog__close {
  color: #9CA3AF !important;
}

::v-deep .el-dialog__close:hover {
  color: #fff !important;
}

::v-deep .el-form-item {
  margin-bottom: 22px;
}

/* 下拉菜单深色主题 */
::v-deep .el-select-dropdown {
  background-color: #2A2A3F !important;
  border-color: #4A4A6A !important;
}

::v-deep .el-select-dropdown__item {
  color: #E5E7EB !important;
  background-color: transparent !important;
}

::v-deep .el-select-dropdown__item:hover,
::v-deep .el-select-dropdown__item.hover {
  background-color: #383850 !important;
  color: #fff !important;
}

::v-deep .el-select-dropdown__item.selected,
::v-deep .el-select-dropdown__item.is-selected {
  color: #6A33F0 !important;
  background-color: #383850 !important;
}

::v-deep .el-select .el-input__suffix .el-select__caret {
  color: #9CA3AF !important;
}

::v-deep .el-select .el-input__suffix .el-select__caret:hover {
  color: #fff !important;
}

::v-deep .el-select.is-focus .el-input__suffix .el-select__caret {
  color: #6A33F0 !important;
}
</style>

<style>
/* 全局样式，用于覆盖 Element UI 下拉菜单（挂载在 body 上） */
.el-select-dropdown {
  background-color: #2A2A3F !important;
  border-color: #4A4A6A !important;
}

.el-select-dropdown__item {
  color: #E5E7EB !important;
  background-color: transparent !important;
}

.el-select-dropdown__item:hover,
.el-select-dropdown__item.hover {
  background-color: #383850 !important;
  color: #fff !important;
}

.el-select-dropdown__item.selected,
.el-select-dropdown__item.is-selected {
  color: #6A33F0 !important;
  background-color: #383850 !important;
}
</style>
